import { GiftCard, giftCardsApi } from '../../services/api';
import { Button } from '../ui/button';
import { TableCell, TableRow } from '../ui/table';
import { cn } from '@/lib/utils';
import { Pencil, History, Download } from 'lucide-react';
import { downloadCertificateImage } from '@/utils/generateCertificateImage';
import { useState } from 'react';
import { useToast } from '@/hooks/useToast';
import { Checkbox } from '../ui/checkbox';

interface GiftCardRowProps {
  card: GiftCard;
  onEdit: (card: GiftCard) => void;
  onViewTransactions?: (card: GiftCard) => void;
  getStatusLabel: (status: string) => string;
  getStatusClass: (status: string) => string;
  isSelected?: boolean;
  onSelect?: (id: string, selected: boolean) => void;
  isBulkMode?: boolean;
}

const statusClasses: Record<string, string> = {
  CAN_BE_USED: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  PARTIALLY_REDEEMED: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
  EXPIRED: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
  REDEEMED: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  INVALID: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
};

export function GiftCardRow({ card, onEdit, onViewTransactions, getStatusLabel, isSelected, onSelect, isBulkMode }: GiftCardRowProps) {
  const [isDownloading, setIsDownloading] = useState(false);
  const { showToast } = useToast();

  const handleDownload = async () => {
    setIsDownloading(true);
    try {
      const initialBalance = parseFloat(card.initialBalance);
      await downloadCertificateImage(card.name, initialBalance);
      // Логируем скачивание в историю
      try {
        await giftCardsApi.logDownload(card.id);
      } catch (logError) {
        // Не показываем ошибку пользователю, если логирование не удалось
        console.error('Ошибка при логировании скачивания:', logError);
      }
      showToast('Сертификат успешно скачан', 'success');
    } catch (error) {
      console.error('Ошибка при скачивании сертификата:', error);
      showToast('Ошибка при скачивании сертификата', 'error');
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <TableRow>
      {isBulkMode && (
        <TableCell>
          <Checkbox
            checked={isSelected || false}
            onCheckedChange={(checked: boolean) => onSelect?.(card.id, checked === true)}
          />
        </TableCell>
      )}
      <TableCell>
        {card.createdAt
          ? new Date(card.createdAt).toLocaleString('ru-RU', {
              year: 'numeric',
              month: '2-digit',
              day: '2-digit',
              hour: '2-digit',
              minute: '2-digit',
            })
          : '-'}
      </TableCell>
      <TableCell>{card.name}</TableCell>
      <TableCell>{parseFloat(card.initialBalance).toFixed(2)} ₽</TableCell>
      <TableCell>{parseFloat(card.currentBalance).toFixed(2)} ₽</TableCell>
      <TableCell>
        <span className={cn("inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold", statusClasses[card.status] || 'bg-gray-100 text-gray-800')}>
          {getStatusLabel(card.status)}
        </span>
      </TableCell>
      <TableCell>
        {card.expireDate
          ? new Date(card.expireDate).toLocaleDateString('ru-RU')
          : 'Не ограничен'}
      </TableCell>
      <TableCell>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="icon"
            onClick={handleDownload}
            title="Скачать сертификат"
            disabled={isDownloading}
          >
            <Download className="h-4 w-4" />
          </Button>
          {onViewTransactions && (
            <Button
              variant="outline"
              size="icon"
              onClick={() => onViewTransactions(card)}
              title="История"
            >
              <History className="h-4 w-4" />
            </Button>
          )}
          <Button
            variant="outline"
            size="icon"
            onClick={() => onEdit(card)}
            title="Изменить"
          >
            <Pencil className="h-4 w-4" />
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );
}

